import { AccessTime, Person } from "@mui/icons-material";
import { Box, Chip, Grid2 as Grid, Skeleton, Stack, Typography } from "@mui/material";
import moment from "moment";
import React from "react";
import { useLocation, useParams, useSearchParams } from "react-router-dom";
import { EntityTimeline, MyPaper, UserAssignmentSection } from "../../components";
import { Association, TagInfo } from "../../interfaces";
import { hexToGrayscale, TAG_CANONICAL_NAMES, UUIDv4 } from "../../utils";
import { getObjectType } from "../listview/addEditListView";

export const Collaborate: React.FC<{
    objectType: Association['object_type'], title: string, tags: TagInfo[], isFetching: boolean,
    users: string[], created_time: string,
    created_user: string, modified_time?: string, modified_user?: string
}> = ({
    title, tags, isFetching, users,
    created_time, created_user, modified_time, modified_user, objectType
}) => {

        const { id } = useParams();
        const searchParams = useSearchParams();

        const location = useLocation();

        const parent_object_type = getObjectType(location);

        const workflow_canonical_name = React.useMemo(() => {
            switch(parent_object_type) {
                case "project":
                    return TAG_CANONICAL_NAMES.project_workflow;
                case "product":
                    return TAG_CANONICAL_NAMES.product_workflow;
                default:
                    return TAG_CANONICAL_NAMES.proposal_workflow;
            }
        }, [parent_object_type]);

        return <Box sx={{ height: "100%", pb: 1, position: "relative" }}>
            <MyPaper padding={0}>
                <>
                    {/* Header */}
                    <Stack sx={{
                        top: 0,
                        left: 0,
                        right: 0,
                        zIndex: 6,
                        position: "absolute",
                        borderTopLeftRadius: 8,
                        borderTopRightRadius: 8,
                        borderBottom: (theme) => `1px solid ${theme.palette.divider}`,
                        backgroundColor: (theme) => theme.palette.background.paper,
                    }}>
                        <Stack direction={"row"} alignItems={"center"} justifyContent={"space-between"} sx={{ width: "100%", px: 2, py: 1 }}>
                            <Stack direction={"column"} sx={{ alignItems: "flex-start" }}>
                                {!isFetching && <>
                                    <Typography variant="body2" sx={{ fontFamily: "htrts_medium" }}>
                                        {`${title ?? ''}`}
                                    </Typography>

                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        gap={0.5}
                                        flexWrap="wrap"
                                        sx={{ mt: 1 }}
                                    >
                                        {tags.map(tag => {
                                            return <Chip variant="outlined" sx={{
                                                fontSize: '0.65rem',
                                                borderColor: tag?.tag_color,
                                                backgroundColor: tag?.tag_color,
                                                height: `20px`,
                                                color: (theme) => `${hexToGrayscale(tag?.tag_color) > 150 ? theme.palette.text.secondary : "#FFFFFF"}`
                                            }}
                                                size={"small"}
                                                label={tag?.tag_name ?? ""}
                                            />
                                        })}
                                    </Stack>
                                </>}

                                {isFetching && <Box sx={{ width: "300px" }}>
                                    <Skeleton width={"60%"} />
                                    <Skeleton width={"100%"} />
                                </Box>}
                            </Stack>
                        </Stack>
                    </Stack>

                    {/* Task Detail */}
                    {!isFetching && <Grid container spacing={1} sx={{ height: "100%", pt: 7, pb: 2 }}>

                        {/* Timeline */}
                        <Grid size={{ xs: 12, sm: 8, md: 8, lg: 8, xl: 9 }} sx={{ height: "100%", paddingTop: 0 }}>
                            {/* Timeline Section */}
                            <Box sx={{ pb: 2 }}>
                                <EntityTimeline objectId={`${id}`} objectType={objectType} />
                            </Box>
                        </Grid>

                        {/* Task Detail */}
                        <Grid size={{ xs: 12, sm: 4, md: 4, lg: 4, xl: 3 }} sx={{ height: "100%", padding: 2, paddingTop: 3 }}>
                            {/* Tags Details */}
                            <Stack gap={2}>

                                {/* User Assignment */}
                                <UserAssignmentSection workflow_canonical_name={workflow_canonical_name} objectId={`${id}`} objectType={objectType} users={users} onUpdated={() => {
                                    searchParams[1]({ reloadTimeline: UUIDv4() })
                                }} />

                                {/* General Information */}
                                <MyPaper>
                                    <Stack gap={2} sx={{ pb: 2 }}>
                                        <Typography gutterBottom variant="body2" sx={{ fontSize: 13, fontFamily: "htrts_medium" }}>General Information</Typography>

                                        {isFetching && <>
                                            <Skeleton width={"100%"} height={'20px'} />
                                            <Skeleton width={"100%"} height={'20px'} />
                                            <Skeleton width={"100%"} height={'20px'} />
                                            <Skeleton width={"100%"} height={'20px'} />
                                        </>}

                                        {!isFetching && [
                                            {
                                                icon: <AccessTime fontSize="small" color="secondary" />,
                                                label: `Created At : \n${moment(created_time).format('lll')}`
                                            },
                                            {
                                                icon: <Person fontSize="small" color="secondary" />,
                                                label: `Created By : ${created_user}`
                                            },
                                            {
                                                icon: <AccessTime fontSize="small" color="secondary" />,
                                                label: `Modified At : ${modified_time ? moment(modified_time).format('lll') : '-'}`
                                            },
                                            {
                                                icon: <Person fontSize="small" color="secondary" />,
                                                label: `Modified By : ${modified_user ?? "-"}`
                                            },
                                        ].map(_ => <Stack gap={1} direction={"row"} alignItems={"center"}>
                                            {_.icon}
                                            <Typography sx={{ fontSize: 13 }} variant="body2" color="textSecondary" >{_.label}</Typography>
                                        </Stack>)}
                                    </Stack>
                                </MyPaper>
                            </Stack>
                        </Grid>
                    </Grid>}
                </>
            </MyPaper>
        </Box>
    };